import { PayoutTransferStatus } from "@modules/finances/types/financesType"
import {
  BalanceTransactionType,
  PayoutDetailsDto,
  PayoutItemDto,
  PayoutTransactionDto,
  TransactionSummaryDto,
  TransferMethod,
} from "@modules/financesPayout/gateway/payoutDto"

export type PayoutItemState = Omit<PayoutItemEntity, "getFormatedDigits">
export class PayoutItemEntity {
  id: string
  amount: number
  currency: string
  status: PayoutTransferStatus
  last4Digits: string | null
  bankName: string | null
  createdAt: Date
  expectedReceptionDate: Date

  private constructor(payoutItemEntity: PayoutItemState) {
    this.id = payoutItemEntity.id
    this.amount = payoutItemEntity.amount
    this.currency = payoutItemEntity.currency
    this.status = payoutItemEntity.status
    this.last4Digits = payoutItemEntity.last4Digits
    this.bankName = payoutItemEntity.bankName
    this.createdAt = payoutItemEntity.createdAt
    this.expectedReceptionDate = payoutItemEntity.expectedReceptionDate
  }

  static fromItemDto(dto: PayoutItemDto): PayoutItemEntity {
    return new PayoutItemEntity({
      id: dto.id,
      amount: dto.amount,
      currency: dto.currency,
      status: dto.status,
      last4Digits: dto.last4Digits ?? null,
      bankName: dto.bankName ?? null,
      createdAt: new Date(dto.createdAt),
      expectedReceptionDate: new Date(dto.expectedReceptionDate),
    })
  }

  getFormatedDigits = (): string => {
    return `····${this.last4Digits}`
  }
}

export class PayoutTransactionEntity {
  id: string
  transactionId: string | null
  type: BalanceTransactionType
  amount: number
  currency: string
  fee: number
  name?: string
  createdAt: Date

  private constructor(payoutTransactionEntity: PayoutTransactionEntity) {
    this.id = payoutTransactionEntity.id
    this.transactionId = payoutTransactionEntity.transactionId
    this.type = payoutTransactionEntity.type
    this.amount = payoutTransactionEntity.amount
    this.currency = payoutTransactionEntity.currency
    this.fee = payoutTransactionEntity.fee
    this.name = payoutTransactionEntity.name
    this.createdAt = payoutTransactionEntity.createdAt
  }

  static fromDto(dto: PayoutTransactionDto): PayoutTransactionEntity {
    return new PayoutTransactionEntity({
      id: dto.id,
      transactionId: dto.transactionId,
      type: dto.type,
      amount: dto.amount,
      currency: dto.currency,
      fee: dto.fee,
      name: dto.name ?? undefined,
      createdAt: new Date(dto.createdAt),
    })
  }
}

export type TransactionSummaryState = Omit<TransactionSummaryEntity, "getTotalLessFee">
export class TransactionSummaryEntity {
  number: number
  feeAmount?: number
  totalAmount?: number
  currency?: string

  private constructor(transactionSummaryEntity: TransactionSummaryState) {
    this.number = transactionSummaryEntity.number
    this.feeAmount = transactionSummaryEntity.feeAmount
    this.totalAmount = transactionSummaryEntity.totalAmount
    this.currency = transactionSummaryEntity.currency
  }

  static fromDto(dto: TransactionSummaryDto): TransactionSummaryEntity {
    return new TransactionSummaryEntity({
      number: dto.number,
      feeAmount: dto.feeAmount ?? undefined,
      totalAmount: dto.totalAmount ?? undefined,
      currency: dto.currency ?? undefined,
    })
  }

  getTotalLessFee(): number {
    return (this.totalAmount ?? 0) - (this.feeAmount ?? 0)
  }
}

export type PayoutDetailsState = Omit<PayoutDetailsEntity, "getFormatedDigits">
export class PayoutDetailsEntity {
  id: string
  amount: number
  currency: string
  status: PayoutTransferStatus
  last4Digits: string | null
  bankName: string | null
  createdAt: Date
  expectedReceptionDate: Date
  intervalStartDate?: Date
  intervalEndDate?: Date
  bankCountry: string
  routingNumber?: string
  transferTrackingId?: string
  note?: string
  transferMethod: TransferMethod
  transactions: PayoutTransactionEntity[]
  paymentSummary: TransactionSummaryEntity
  refundSummary: TransactionSummaryEntity
  adjustmentSummary: TransactionSummaryEntity

  private constructor(payoutDetailsEntity: PayoutDetailsState) {
    this.id = payoutDetailsEntity.id
    this.amount = payoutDetailsEntity.amount
    this.currency = payoutDetailsEntity.currency
    this.status = payoutDetailsEntity.status
    this.last4Digits = payoutDetailsEntity.last4Digits
    this.bankName = payoutDetailsEntity.bankName
    this.createdAt = payoutDetailsEntity.createdAt
    this.expectedReceptionDate = payoutDetailsEntity.expectedReceptionDate
    this.intervalStartDate = payoutDetailsEntity.intervalStartDate
    this.intervalEndDate = payoutDetailsEntity.intervalEndDate
    this.bankCountry = payoutDetailsEntity.bankCountry
    this.routingNumber = payoutDetailsEntity.routingNumber
    this.transferTrackingId = payoutDetailsEntity.transferTrackingId
    this.note = payoutDetailsEntity.note
    this.transferMethod = payoutDetailsEntity.transferMethod
    this.transactions = payoutDetailsEntity.transactions
    this.paymentSummary = payoutDetailsEntity.paymentSummary
    this.refundSummary = payoutDetailsEntity.refundSummary
    this.adjustmentSummary = payoutDetailsEntity.adjustmentSummary
  }

  static fromDto(dto: PayoutDetailsDto): PayoutDetailsEntity {
    return new PayoutDetailsEntity({
      id: dto.id,
      amount: dto.amount,
      currency: dto.currency,
      status: dto.status,
      last4Digits: dto.last4Digits ?? null,
      bankName: dto.bankName ?? null,
      createdAt: new Date(dto.createdAt),
      expectedReceptionDate: new Date(dto.expectedReceptionDate),
      intervalStartDate: dto.intervalStartDate ? new Date(dto.intervalStartDate) : undefined,
      intervalEndDate: dto.intervalEndDate ? new Date(dto.intervalEndDate) : undefined,
      bankCountry: dto.bankCountry,
      routingNumber: dto.routingNumber ?? undefined,
      transferTrackingId: dto.transferTrackingId ?? undefined,
      note: dto.note ?? undefined,
      transferMethod: dto.transferMethod,
      transactions: dto.transactions.map((transaction) => PayoutTransactionEntity.fromDto(transaction)),
      paymentSummary: TransactionSummaryEntity.fromDto(dto.paymentSummary),
      refundSummary: TransactionSummaryEntity.fromDto(dto.refundSummary),
      adjustmentSummary: TransactionSummaryEntity.fromDto(dto.adjustmentSummary),
    })
  }

  getFormatedDigits = (): string => {
    return `····${this.last4Digits}`
  }
}
